import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Building2, Users, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const inputCls =
  "h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function Labeled({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-600">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function DeveloperPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [factories, setFactories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [factoryModal, setFactoryModal] = useState({ open: false, item: null });
  const [userModal, setUserModal] = useState({ open: false, item: null });
  const [del, setDel] = useState(null); // {type, item}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, u] = await Promise.all([api.get("/factories"), api.get("/users")]);
      setFactories(f.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const factoryName = (fid) => factories.find((f) => f.id === fid)?.name || "—";

  const confirmDelete = async () => {
    try {
      if (del.type === "factory") await api.delete(`/factories/${del.item.id}`);
      else await api.delete(`/users/${del.item.id}`);
      toast.success("Deleted");
      setDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (user?.role !== "developer") {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNav />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Developer Portal</h1>
            <p className="mt-1 text-lg text-slate-500">Manage factories and users</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <Tabs defaultValue="factories" className="mt-8">
            <TabsList className="h-12">
              <TabsTrigger data-testid="tab-factories" value="factories" className="gap-2 px-5 text-base">
                <Building2 className="h-5 w-5" /> Factories ({factories.length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-users" value="users" className="gap-2 px-5 text-base">
                <Users className="h-5 w-5" /> Users ({users.length})
              </TabsTrigger>
            </TabsList>

            {/* Factories */}
            <TabsContent value="factories" className="mt-6">
              <div className="flex justify-end">
                <Button data-testid="add-factory-btn" onClick={() => setFactoryModal({ open: true, item: null })} className="h-12 gap-2 bg-blue-600 px-6 hover:bg-blue-700">
                  <Plus className="h-5 w-5" /> New Factory
                </Button>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 text-sm uppercase tracking-wide text-slate-500">
                      <th className="px-6 py-4 font-semibold">Factory name</th>
                      <th className="px-4 py-4 font-semibold">Code</th>
                      <th className="px-4 py-4 font-semibold">Location</th>
                      <th className="px-4 py-4 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factories.map((f) => (
                      <tr key={f.id} data-testid={`factory-row-${f.id}`} className="border-t border-slate-100 text-base text-slate-700 hover:bg-slate-50">
                        <td className="px-6 py-4 font-semibold text-slate-800">{f.name}</td>
                        <td className="px-4 py-4">{f.code || "—"}</td>
                        <td className="px-4 py-4">{f.location || "—"}</td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-1">
                            <button data-testid={`edit-factory-${f.id}`} onClick={() => setFactoryModal({ open: true, item: f })} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200"><Pencil className="h-4 w-4" /></button>
                            <button data-testid={`delete-factory-${f.id}`} onClick={() => setDel({ type: "factory", item: f })} className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Users */}
            <TabsContent value="users" className="mt-6">
              <div className="flex justify-end">
                <Button data-testid="add-user-btn" onClick={() => setUserModal({ open: true, item: null })} className="h-12 gap-2 bg-blue-600 px-6 hover:bg-blue-700">
                  <Plus className="h-5 w-5" /> New User
                </Button>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 text-sm uppercase tracking-wide text-slate-500">
                      <th className="px-6 py-4 font-semibold">Full name</th>
                      <th className="px-4 py-4 font-semibold">Email</th>
                      <th className="px-4 py-4 font-semibold">Role</th>
                      <th className="px-4 py-4 font-semibold">Factory</th>
                      <th className="px-4 py-4 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100 text-base text-slate-700 hover:bg-slate-50">
                        <td className="px-6 py-4 font-semibold text-slate-800">{u.full_name}</td>
                        <td className="px-4 py-4">{u.email}</td>
                        <td className="px-4 py-4">
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">{ROLE_LABELS[u.role]}</span>
                        </td>
                        <td className="px-4 py-4">{u.factory_id ? factoryName(u.factory_id) : "—"}</td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-1">
                            <button data-testid={`edit-user-${u.id}`} onClick={() => setUserModal({ open: true, item: u })} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200"><Pencil className="h-4 w-4" /></button>
                            <button data-testid={`delete-user-${u.id}`} onClick={() => setDel({ type: "user", item: u })} className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      <FactoryModal state={factoryModal} onClose={() => setFactoryModal({ open: false, item: null })} onSaved={load} />
      <UserModal state={userModal} factories={factories} onClose={() => setUserModal({ open: false, item: null })} onSaved={load} />

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {del?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              {del?.type === "factory"
                ? "Factories with users or orders cannot be deleted."
                : "This will permanently remove the user account."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FactoryModal({ state, onClose, onSaved }) {
  const { open, item } = state;
  const [form, setForm] = useState({ name: "", code: "", location: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(item ? { name: item.name, code: item.code || "", location: item.location || "" } : { name: "", code: "", location: "" });
  }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Factory name is required");
    setSaving(true);
    try {
      if (item) await api.put(`/factories/${item.id}`, form);
      else await api.post("/factories", form);
      toast.success("Factory saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{item ? "Edit Factory" : "New Factory"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Labeled label="Factory name" required>
            <input data-testid="factory-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Labeled>
          <Labeled label="Code">
            <input data-testid="factory-code-input" className={inputCls} value={form.code} onChange={(e) => set("code", e.target.value)} />
          </Labeled>
          <Labeled label="Location">
            <input data-testid="factory-location-input" className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} />
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 px-6" onClick={onClose}>Cancel</Button>
          <Button data-testid="factory-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" disabled={saving} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserModal({ state, factories, onClose, onSaved }) {
  const { open, item } = state;
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", role: "merchandiser", factory_id: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open)
      setForm(item
        ? { full_name: item.full_name, email: item.email, password: "", phone: item.phone || "", role: item.role, factory_id: item.factory_id || "" }
        : { full_name: "", email: "", password: "", phone: "", role: "merchandiser", factory_id: "" });
  }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.full_name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (!item && !form.password) return toast.error("Password is required for new users");
    if (form.role !== "developer" && !form.factory_id) return toast.error("Please assign a factory");
    setSaving(true);
    try {
      const payload = { ...form, factory_id: form.role === "developer" ? null : form.factory_id };
      if (item) {
        const body = { ...payload };
        if (!body.password) delete body.password;
        await api.put(`/users/${item.id}`, body);
      } else {
        await api.post("/users", payload);
      }
      toast.success("User saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{item ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
          <Labeled label="Full name" required>
            <input data-testid="user-name-input" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </Labeled>
          <Labeled label="Phone">
            <input data-testid="user-phone-input" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Labeled>
          <Labeled label="Email" required>
            <input data-testid="user-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Labeled>
          <Labeled label={item ? "Password (leave blank to keep)" : "Password"} required={!item}>
            <input data-testid="user-password-input" type="password" className={inputCls} value={form.password} onChange={(e) => set("password", e.target.value)} />
          </Labeled>
          <Labeled label="Role" required>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select" className="h-12 text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="developer">Developer</SelectItem>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="senior_merchandiser">Senior Merchandiser</SelectItem>
                <SelectItem value="merchandiser">Merchandiser</SelectItem>
              </SelectContent>
            </Select>
          </Labeled>
          <Labeled label="Factory" required={form.role !== "developer"}>
            <Select value={form.factory_id} onValueChange={(v) => set("factory_id", v)} disabled={form.role === "developer"}>
              <SelectTrigger data-testid="user-factory-select" className="h-12 text-base"><SelectValue placeholder="Select factory" /></SelectTrigger>
              <SelectContent>
                {factories.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 px-6" onClick={onClose}>Cancel</Button>
          <Button data-testid="user-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" disabled={saving} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
